import {
  WORKSHOP_BENCHMARK_IDS,
  WORKSHOP_BOOK_TRACK_IDS,
  WORKSHOP_ONBOARDING_QUEST_IDS,
  ACTIVITY_STATUS,
  VOCAB_PROGRESS,
  SEASON_PART,
  ITEMS,
} from 'a/hub/data/constants';
import WorkshopHardcodedGamesWBB from 'a/hub/data/WorksopHardcodedGamesWBB.json';
import WorkshopEreaderWbbBp from 'a/hub/data/WorkshopEreaderWBB_BP.json';
import NormalWorkshop from 'a/hub/data/WorkshopQuests.json';
import { getTimestamp } from 'root/lib/DatetimeManager';
import { getBucketData } from 'lib/utilityFunctions';
import { logRumError } from 'root/lib/DDRum';
import gameIds from '../data/GameIds';
import { getWorkshopStepConfig } from '../data/workshopSteps';

/**
 * Updates the target game to have all levels marked as mastered, uses skillId to identify the game.
 * @param {object[]} games - the list of games in the workshop
 * @param {string} skillId - the target skill id to update
 * @returns {object[]} - the updated list of games
 * */
export const masterAllLevelsOfTargetSkillId = (games, skillId) => games.map((game) =>
  (game.skillId === skillId ? { ...game, levelsMastered: [...game.levels] } : game),
);

/**
 * Gets the quest key from the quest id, if the quest id is 0, it returns 'challengeQuest'
 * @param {number} questId - the quest id to get the key from
 * @returns {string} - the quest key, either 'challengeQuest' or 'quest{questId}'
 * */
export const getQuestKeyFromQuestId = (questId = 0) => {
  if (questId === 0) {
    return 'challengeQuest';
  }
  return `quest${questId}`;
};

/**
 * Clears the failure counters for the target game levels
 * @param {object} gameFailures - the game failures object, ie, { gameName_level: 1, gameName_level2: 2 }
 * @param {string} gameName - the name of the game
 * @param {string[]} levels - the levels to clear the failure counters for
 * @returns {object} - the updated game failures object
 * */
export const clearFailureCountersForTargetGameLevels = (gameFailures, gameName, levels) => {
  const targetKeys = levels.map((level) => `${gameName}_${level}`);
  const newFailures = { ...gameFailures };
  targetKeys.forEach((key) => {
    delete newFailures[key];
  });
  return newFailures;
};

/**
 * Gets the index of the activity from the activity id, from the format, ie 'activity_1' -> 1
 * @param {string} targetActivityId - the activity id to get the index from, ie 'activity_1'
 * @returns {number} - the index of the activity
 * */
export const getIndexFromActivityId = (targetActivityId) => {
  const [, number] = targetActivityId.match(/_(\d+)$/) || [];
  return parseInt(number, 10) - 1;
};

/**
 * Vocab session id contains the quest id which contains the vocab episode number, ie, '301-1' => 01, '302-1' => 02, etc,
 * @param {string} vocabSessionId - the vocab session id to get the episode from, ie, '301-1'
 * @returns {string} - the episode number, ie, '01', '02', etc
 * */
export const getEpisodeFromVocabSessionId = (vocabSessionId) => vocabSessionId.split('-')[0].slice(-2);

/**
 * Returns a shallow copy of workshop game entries with independent levels and levelMastered arrays.
 * @param {object[]} games - workshop games to clone
 * @returns {object[]} - cloned games safe to mutate without affecting the source
 */
export const cloneWorkshopGames = (games = []) =>
  games.map(game => ({
    ...game,
    levels: [...(game.levels || [])],
    levelsMastered: [...(game.levelsMastered || [])],
  }));

/**
 * Checks the workshop object for the necesary games lenth to work with the current benchmark,
 * if the games length is not valid for the current benchmark, it replaces them with default games.
 * @param {object} workshop - the workshop object containing games by quest
 * @param {string} benchmark - the benchmark id, either 'NORMAL' or 'WELL_BELOW'
 * @returns {object} - the updated workshop object with default games if needed
 * */
export const initWorkshopGames = (workshop, benchmark) => {
  if (benchmark === WORKSHOP_BENCHMARK_IDS.NORMAL) {
    const normalWorkshop = { ...workshop };
    Object.keys(workshop).forEach((questKey) => {
      const quest = workshop[questKey];
      // 3 games are required per quest on normal benchmark
      // if we do not have 3 games in a quest it means we are coming from a different benchmark, so we replace the list with NORMAL default games
      if (quest.games.length !== 3) {
        normalWorkshop[questKey] = {
          ...quest,
          games: cloneWorkshopGames(
            NormalWorkshop.workshop[questKey]?.games || quest.games,
          ),
        };
      }
    });
    return normalWorkshop;
  }

  const wellBelowWorkshop = { ...workshop };
  Object.keys(workshop).forEach((questKey) => {
    const quest = workshop[questKey];
    // 6 games are required per quest on well below benchmark
    // if we do not have 6 games in a quest it means we are coming from a different benchmark, so we replace the set with WBB default games
    if (quest.games.length !== 6) {
      wellBelowWorkshop[questKey] = {
        ...quest,
        games: cloneWorkshopGames(
          WorkshopHardcodedGamesWBB[questKey] || quest.games,
        ),
      };
    }
  });
  return wellBelowWorkshop;
};

/**
 * Seeds the eReader book of every quest from the mapping that matches the given book track.
 * Only the book identity is replaced - progress fields (status, useStaticAsset) are preserved.
 * Quests the mapping has no entry for fall back to the normal-track book, and are skipped when that
 * has no bookId, ie, the challenge quest. On the NORMAL track the mapping is empty, so every quest
 * takes that fallback and keeps the books it was seeded with from WorkshopQuests.json.
 * Unlike initWorkshopGames this never repairs a student's saved state: it is only ever applied to
 * freshly initialized local state, so that a student already mid-Workshop keeps the books they started on.
 * @param {object} workshop - the workshop object containing an eReader block per quest
 * @param {string} bookTrack - the book track id, either 'NORMAL' or 'WBB_BP'
 * @returns {object} - the workshop object with the book track's books seeded per quest
 * */
export const initWorkshopEreader = (workshop, bookTrack) => {
  const books = bookTrack === WORKSHOP_BOOK_TRACK_IDS.WBB_BP ? WorkshopEreaderWbbBp : {};
  const updatedWorkshop = { ...workshop };

  Object.keys(workshop).forEach((questKey) => {
    const quest = workshop[questKey];
    const book = books[questKey] || NormalWorkshop.workshop[questKey]?.eReader;
    if (!quest.eReader || !book?.bookId) {
      return;
    }
    const { bookId, bookName, chapter, skillId } = book;
    updatedWorkshop[questKey] = {
      ...quest,
      eReader: { ...quest.eReader, bookId, bookName, chapter, skillId },
    };
  });

  return updatedWorkshop;
};

/**
 * Fixes corrupted placeholder games in a quest by replacing them with hardcoded games.
 * Placeholder games have empty game names, skillIds, and levels, which can block student progress.
 * @param {object} quest - the quest object containing games array
 * @param {string} questKey - the quest key, ie, 'quest301', 'quest302', etc
 * @param {string} benchmark - the benchmark id, either 'NORMAL' or 'WELL_BELOW'
 * @returns {object} - the quest object with placeholder games replaced by hardcoded games
 * */
export const fixPlaceholderGames = (quest, questKey, benchmark) => {
  const hardcodedGames = benchmark === WORKSHOP_BENCHMARK_IDS.WELL_BELOW
    ? WorkshopHardcodedGamesWBB[questKey] || []
    : NormalWorkshop.workshop[questKey]?.games || [];

  const gamesCount = benchmark === WORKSHOP_BENCHMARK_IDS.WELL_BELOW ? 6 : 3;

  const corruptedPositions = []; // Track which positions had placeholders

  const fixedGames = Array.from({ length: gamesCount }, (_, index) => {
    const game = quest.games[index];

    // Replace if it's a placeholder (source === 'placeholder' or empty game fields)
    if (!game || game.source === 'placeholder' || !game.game || !game.skillId) {
      corruptedPositions.push(index);
      return hardcodedGames[index] || game;
    }

    return game;
  });

  // Log to DataDog if any placeholders were fixed
  if (corruptedPositions.length > 0) {
    logRumError('Workshop corruption: Placeholder games detected and replaced', {
      questKey,
      benchmark,
      corruptedCount: corruptedPositions.length,
      corruptedPositions,
      totalGames: quest.games.length,
      fixSource: benchmark === WORKSHOP_BENCHMARK_IDS.WELL_BELOW ? 'WorkshopHardcodedGamesWBB.json' : 'NormalWorkshop',
    });
  }

  return {
    ...quest,
    games: fixedGames,
  };
};

/**
 * Fixes corrupted hardcoded games by validating skillId→levels mapping against reference data.
 * Some saved games have incorrect levels for their skillId due to past bugs.
 * This function identifies mismatches and replaces with correct hardcoded values.
 * Preserves progress for completed games to avoid resetting student work.
 * @param {object} quest - the quest object containing games array
 * @param {string} questKey - the quest key, ie, 'quest301', 'quest302', etc
 * @param {string} benchmark - the benchmark id, either 'NORMAL' or 'WELL_BELOW'
 * @returns {object} - the quest object with corrected hardcoded games
 * */
export const fixCorruptedHardcodedGames = (quest, questKey, benchmark) => {
  const hardcodedGames = benchmark === WORKSHOP_BENCHMARK_IDS.WELL_BELOW
    ? WorkshopHardcodedGamesWBB[questKey] || []
    : NormalWorkshop.workshop[questKey]?.games || [];

  // Create lookup: skillId → correct game data
  const correctGameBySkillId = hardcodedGames.reduce((map, game) => {
    if (game.skillId) {
      // eslint-disable-next-line no-param-reassign
      map[game.skillId] = game;
    }
    return map;
  }, {});

  const corruptionDetails = []; // Track corrupted games

  const fixedGames = quest.games.map((game, index) => {
    // Skip non-hardcoded games
    if (!['hard-coded', 'hardcoded'].includes(game.source)) {
      return game;
    }

    const correctGame = correctGameBySkillId[game.skillId];
    if (!correctGame) {
      return game;
    }

    // Check if levels match
    const levelsMatch = game.levels.length === correctGame.levels.length &&
      game.levels.every((level, idx) => level === correctGame.levels[idx]);

    if (levelsMatch) {
      return game; // Already correct
    }

    // Corruption detected - check if game is complete
    const isCompleted = game.levelsMastered.length > 0 &&
      game.levelsMastered.length === game.levels.length;

    if (isCompleted) {
      // Preserve completed game even with wrong data
      corruptionDetails.push({
        position: index,
        skillId: game.skillId,
        action: 'preserved',
        reason: 'game_complete',
        wrongLevels: game.levels,
        correctLevels: correctGame.levels,
      });
      return game;
    }

    // Reset incomplete game with wrong levels
    corruptionDetails.push({
      position: index,
      skillId: game.skillId,
      action: 'reset',
      reason: 'incomplete_with_wrong_levels',
      wrongLevels: game.levels,
      correctLevels: correctGame.levels,
      progressLost: game.levelsMastered || [],
    });

    return { ...correctGame, levelsMastered: [] };
  });

  // Log to DataDog if any corruption was detected
  if (corruptionDetails.length > 0) {
    const preservedCount = corruptionDetails.filter(d => d.action === 'preserved').length;
    const resetCount = corruptionDetails.filter(d => d.action === 'reset').length;

    logRumError('Workshop corruption: Hardcoded games with wrong levels detected', {
      questKey,
      benchmark,
      totalCorrupted: corruptionDetails.length,
      gamesPreserved: preservedCount,
      gamesReset: resetCount,
      corruptionDetails, // Full details for debugging
    });
  }

  return { ...quest, games: fixedGames };
};

/**
 * Calculate the benchmark based on the placement period and grade
 * @param {string} placementPeriod - the placement period, ie, 'AP'
 * @param {string} placementGrade - the placement grade, ie, '01', 02, etc
 * @returns {string} - the benchmark id, either 'NORMAL' or 'WELL_BELOW'
 * */
export const calculateBenchmark = (placementPeriod = '', placementGrade = '') => {
  const isWellBelowBenchmark = ['AP04', 'AP05', 'BP04', 'BP05'].includes(`${placementPeriod}${placementGrade}`);
  return isWellBelowBenchmark ? WORKSHOP_BENCHMARK_IDS.WELL_BELOW : WORKSHOP_BENCHMARK_IDS.NORMAL;
};

/**
 * Calculate which eReader books a student gets based on the placement period and grade.
 * Narrower than calculateBenchmark on purpose: AP04/AP05 are well below benchmark for games
 * but keep the normal books, so only BP04/BP05 get the simpler book track.
 * @param {string} placementPeriod - the placement period, ie, 'BP'
 * @param {string} placementGrade - the placement grade, ie, '04', '05', etc
 * @returns {string} - the book track id, either 'NORMAL' or 'WBB_BP'
 * */
export const calculateBookTrack = (placementPeriod = '', placementGrade = '') => {
  const placement = `${placementPeriod}${placementGrade}`;
  return ['BP04', 'BP05'].includes(placement) ? WORKSHOP_BOOK_TRACK_IDS.WBB_BP : WORKSHOP_BOOK_TRACK_IDS.NORMAL;
};

/**
 * Updates the activity status in the workshop state
 * @param {object} state - the current state of the workshop
 * @param {string} activity - the arget activity to update, either 'battery', 'vocab', 'eReader' or 'TFE'
 * @param {object} payload - the payload containing the questId and status
 * @param {string} payload.questId - the quest id to update
 * @param {string} payload.status - the status to set, either 'active', 'inactive' or 'finished'
 * @returns {object} - the updated state of the workshop
 * */
export const updateActivityStatus = (state, activity, { questId, status }) => {
  const questKey = getQuestKeyFromQuestId(questId);
  const quest = state.workshop[questKey];

  const shouldRemoveStaticAsset = quest[activity].useStaticAsset && status !== ACTIVITY_STATUS.INACTIVE;
  return {
    ...state,
    workshop: {
      ...state.workshop,
      [questKey]: {
        ...quest,
        [activity]: {
          ...quest[activity],
          useStaticAsset: shouldRemoveStaticAsset ? false : quest[activity].useStaticAsset,
          status,
        },
        startDate: quest.startDate || getTimestamp(),
        updated: getTimestamp(),
      },
    },
  };
};

/**
 * Updates the vocab progress in the workshop state based on incoming vocab progress data
 * @param {object} state - the current state of the workshop
 * @param {object} vocabData - the incomming data containing the vocab progress
 * @param {object} vocabData.questProgress - the vocab progress data
 * @returns {object} - the updated state of the workshop
 * */
export const updateVocabProgress = (state, { questProgress: vocabProgress = {} }) => {
  const questKey = getQuestKeyFromQuestId(state.currentQuestId);
  const currentQuest = { ...state.workshop[questKey] };
  const { sessions = [] } = currentQuest.vocab;

  currentQuest.vocab.sessions = sessions.map((session) => ({
    ...session,
    // only update if VOCAB_PROGRESS.COMPLETE, otherwise keep the current progress
    progress: vocabProgress[session.id] === VOCAB_PROGRESS.COMPLETE ? VOCAB_PROGRESS.COMPLETE : session.progress,
  }));

  return {
    ...state,
    workshop: {
      ...state.workshop,
      [questKey]: currentQuest,
    },
  };
};

/**
 * Updates the eReader progress in the workshop state based on incoming ereader data
 * @param {object} state - the current state of the workshop
 * @param {object} ereaderData - the incomming data containing the ereader progress
 * @returns {object} - the updated state of the workshop
 * */
export const updateEreaderProgress = (state, ereaderData) => {
  const questKey = getQuestKeyFromQuestId(state.currentQuestId);
  const currentQuest = { ...state.workshop[questKey] };
  const { chapter, bookId } = currentQuest.eReader;
  const { gameDetails } = ereaderData[bookId] || {};
  if (gameDetails) {
    const { questOverride, status } = gameDetails;
    let eReaderStatus;
    if (chapter) {
      // chapter progress
      if (questOverride && questOverride[chapter]) {
        eReaderStatus = questOverride[chapter].status;
      }
    } else {
      // full book progress
      eReaderStatus = status;
    }
    currentQuest.eReader.progress = eReaderStatus || currentQuest.eReader.progress;
  }
  return {
    ...state,
    workshop: {
      ...state.workshop,
      [questKey]: currentQuest,
    },
  };
};

/**
 * Updates the TFE progress in the workshop state based on incoming TFE progress data
 * @param {object} state - the current state of the workshop
 * @param {object} TFEProgress - the incomming data containing the TFE progress
 * @returns {object} - the updated state of the workshop
 * */
export const updateTFEProgress = (state, TFEProgress) => {
  const questKey = getQuestKeyFromQuestId(state.currentQuestId);
  const currentQuest = { ...state.workshop[questKey] };

  const { TFE: { episode: { id, parts } } } = state.workshop[questKey];

  // update full episode progress
  currentQuest.TFE.episode.progress = TFEProgress[id] || currentQuest.TFE.episode.progress;

  // update episode parts progress
  const episodePartsProgress = TFEProgress[`${id}_parts`] || {};
  currentQuest.TFE.episode.parts = parts.map(part => {
    const partProgress = episodePartsProgress[part.id] || part.progress;
    return {
      ...part,
      progress: partProgress,
    };
  });

  return {
    ...state,
    workshop: {
      ...state.workshop,
      [questKey]: currentQuest,
    },
  };
};

/**
 * Extracts and processes workshop history from action data, returning only the history
 * up to the last completed quest. This prevents overwriting future quests if history data
 * is either obsolete or corrupted.
 * @param {object} stateData - the state data containing workshop and history buckets
 * @returns {object} - the filtered history object containing only relevant quest data
 */
export const getWorkshopHistoryFromBucket = (stateData) => {
  const { history: workshopHistory = {} } = getBucketData(stateData[gameIds.METAGAME_WORKSHOP_HISTORY]);
  const { currentQuestId, lastQuestFinishedBeforeEndOfQuests, seasonPart } = getBucketData(
    stateData[gameIds.METAGAME_WORKSHOP],
  );

  const lastCompletedQuest = currentQuestId === 0 ? lastQuestFinishedBeforeEndOfQuests : currentQuestId - 1; // currentQuestId is not completed yet
  const historyToBeUsed = {};

  let questPointer = seasonPart === SEASON_PART.A ? 301 : 309;

  // Copy history from the starting quest up to the last completed quest.
  // Skip undefined entries to avoid overwriting local quest data when history is empty or incomplete.
  while (questPointer <= lastCompletedQuest) {
    const questData = workshopHistory[`quest${questPointer}`];
    if (questData !== undefined) {
      historyToBeUsed[`quest${questPointer}`] = questData;
    }
    questPointer += 1;
  }

  return historyToBeUsed;
};

/**
 * Fixes corrupted workshop step where step is 0 but all content is complete.
 * This corruption occurred when advanceToNextWorkshopStep() was called from the last step (e.g., step 13)
 * which had nextStep: 0, causing the step to incorrectly advance from 13 to 0.
 * @param {number} workshopStep - current workshop step from state
 * @param {object} quest - the current quest object with games, TFE, vocab, eReader
 * @param {number} currentQuestId - the current quest ID (used to determine if onboarding)
 * @param {string} benchmark - the benchmark level (NORMAL or WELL_BELOW)
 * @returns {number} - the corrected workshop step, or original if no corruption detected
 */
export const fixCorruptedWorkshopStep = (workshopStep, quest, currentQuestId, benchmark) => {
  // Only fix if step is 0 (potential corruption)
  if (workshopStep !== 0 || !quest) {
    return workshopStep;
  }

  // Check if all content is complete
  const allContentComplete = (
    quest.games?.every(g => g.levelsMastered?.length >= g.levels?.length) &&
    quest.TFE?.episode?.progress === 'complete' &&
    quest.vocab?.sessions?.every(s => s.progress === 'finished') &&
    quest.eReader?.progress === 'complete'
  );

  if (!allContentComplete) {
    return workshopStep;
  }

  // Corruption detected: all content complete but step is 0
  // Calculate correct last step based on quest type and benchmark
  const isWellBelow = benchmark === WORKSHOP_BENCHMARK_IDS.WELL_BELOW;
  const isOnboardingQuest = WORKSHOP_ONBOARDING_QUEST_IDS.includes(currentQuestId);

  let correctStep;
  if (isOnboardingQuest) {
    correctStep = isWellBelow ? 22 : 17;
  } else {
    correctStep = isWellBelow ? 18 : 13;
  }

  return correctStep;
};

/**
 * Returns the reward step for a student parked on the curioso step without the quest's portal reward,
 * otherwise the step unchanged.
 * @param {number} workshopStep
 * @param {string[]} tfeRewards - rewards.TFE from the saved bucket
 * @param {number} currentQuestId
 * @param {string} benchmark
 * @returns {number}
 */
export const fixMissingRewardAtCuriosoStep = (workshopStep, tfeRewards = [], currentQuestId, benchmark) => {
  const isOnboarding = WORKSHOP_ONBOARDING_QUEST_IDS.includes(currentQuestId);
  const stepConfig = getWorkshopStepConfig(workshopStep, benchmark, isOnboarding);
  const previousStepConfig = getWorkshopStepConfig(workshopStep - 1, benchmark, isOnboarding);
  if (!stepConfig?.resetCurioso || previousStepConfig?.TFE !== ACTIVITY_STATUS.REWARD) {
    return workshopStep;
  }

  const rewardName = ITEMS.portal[currentQuestId]?.name;
  if (!rewardName || tfeRewards.includes(rewardName)) {
    return workshopStep;
  }

  return workshopStep - 1;
};

/**
 * Counts the number of completed games (all levels mastered).
 * @param {object[]} games - Array of game objects with levels and levelsMastered
 * @returns {number} - Count of games where all levels are mastered
 */
const countCompletedGames = (games = []) => (
  Array.isArray(games) ? games.filter(g => g.levelsMastered?.length >= g.levels?.length).length : 0
);

/**
 * Counts the number of completed vocabulary sessions.
 * @param {object} vocab - Vocab object containing sessions array
 * @returns {number} - Count of sessions with 'finished' progress
 */
const countCompletedVocab = (vocab = {}) => {
  if (!vocab.sessions || !Array.isArray(vocab.sessions)) {
    return 0;
  }
  return vocab.sessions.filter(s => s.progress === VOCAB_PROGRESS.COMPLETE).length;
};

/**
 * Counts completed TFE activities based on benchmark.
 * For NORMAL: Counts episode-level completion (0 or 1).
 * For WELL_BELOW: Counts part-level completions (0-3).
 * @param {object} tfe - TFE object containing episode and parts
 * @param {string} benchmark - Benchmark level (NORMAL or WELL_BELOW)
 * @returns {number} - Count of completed TFE activities
 */
const countCompletedTFE = (tfe = {}, benchmark) => {
  if (!tfe.episode) {
    return 0;
  }

  // NORMAL: Count episode-level completion
  if (benchmark === WORKSHOP_BENCHMARK_IDS.NORMAL) {
    return tfe.episode.progress === 'complete' ? 1 : 0;
  }

  // WELL_BELOW: Count part-level completions
  // If episode is complete, all 3 parts are complete (per modalSystemActions.js:210)
  if (tfe.episode.progress === 'complete') {
    return 3;
  }

  // Otherwise, count individual part completions
  if (!tfe.episode.parts || !Array.isArray(tfe.episode.parts)) {
    return 0;
  }
  return tfe.episode.parts.filter(part => part.progress === 'complete').length;
};

/**
 * Counts completed eReader activities.
 * @param {object} ereader - eReader object with progress property
 * @returns {number} - 1 if complete, 0 otherwise
 */
const countCompletedEreader = (ereader = {}) => (ereader.progress === 'complete' ? 1 : 0);

/**
 * Validates workshop data integrity by comparing expected activity completions
 * (derived from step progression) against actual completion state.
 * Reports mismatches via DataDog for monitoring (non-blocking).
 *
 * @param {object} params - Validation parameters
 * @param {number} params.workshopStep - Current workshop step
 * @param {number} params.currentQuestId - Current quest ID (e.g., 301, 314)
 * @param {string} params.benchmark - Benchmark level (NORMAL or WELL_BELOW)
 * @param {object} params.quest - Quest object containing games, TFE, vocab, eReader
 * @param {string} params.validationPoint - When validation runs ('beforeSave' or 'afterLoad')
 * @returns {object} - { isValid, expected, actual, mismatches }
 */
export const validateWorkshopDataIntegrity = ({
  workshopStep,
  currentQuestId,
  benchmark,
  quest,
  validationPoint,
}) => {
  // Guard check: Missing quest data is a data integrity problem and should be flagged
  if (!quest) {
    logRumError('Workshop data integrity error: Quest data is missing', {
      questId: currentQuestId,
      workshopStep,
      benchmark,
      validationPoint,
    });
    return {
      isValid: false,
      expected: {},
      actual: {},
      mismatches: {
        quest: true,
      },
    };
  }

  // Determine if onboarding quest
  const isOnboarding = WORKSHOP_ONBOARDING_QUEST_IDS.includes(currentQuestId);

  // Count expected activities by iterating through completed steps (0 to workshopStep exclusive)
  let expectedGames = 0;
  let expectedVocab = 0;
  let expectedTFE = 0;
  let expectedEreader = 0;

  // Iterate only through completed steps (current step prompts for incomplete work)
  for (let step = 0; step < workshopStep; step += 1) {
    const stepConfig = getWorkshopStepConfig(step, benchmark, isOnboarding);

    // Skip steps without targetActivityId (transmissions, rewards, intro sequences)
    if (!stepConfig || !stepConfig.targetActivityId) {
      // eslint-disable-next-line no-continue
      continue;
    }

    const { targetActivityId } = stepConfig;

    // Count expected activities by type
    if (targetActivityId.startsWith('skill_game_')) {
      expectedGames += 1;
    } else if (targetActivityId.startsWith('vocabulary_')) {
      expectedVocab += 1;
    } else if (targetActivityId.startsWith('close_reading_')) {
      expectedTFE += 1;
    } else if (targetActivityId.startsWith('ereader_')) {
      expectedEreader += 1;
    }
  }

  // Count actual completions
  const actualGames = countCompletedGames(quest.games);
  const actualVocab = countCompletedVocab(quest.vocab);
  const actualTFE = countCompletedTFE(quest.TFE, benchmark);
  const actualEreader = countCompletedEreader(quest.eReader);

  // Build results objects
  const expected = {
    games: expectedGames,
    vocab: expectedVocab,
    tfe: expectedTFE,
    ereader: expectedEreader,
  };

  const actual = {
    games: actualGames,
    vocab: actualVocab,
    tfe: actualTFE,
    ereader: actualEreader,
  };

  // Detect mismatches (both under and over indicate corruption)
  const mismatches = {
    games: actual.games !== expected.games,
    vocab: actual.vocab !== expected.vocab,
    tfe: actual.tfe !== expected.tfe,
    ereader: actual.ereader !== expected.ereader,
  };

  const hasMismatches = Object.values(mismatches).some(mismatch => mismatch);

  // Report to DataDog if mismatches found
  if (hasMismatches) {
    logRumError('Workshop data integrity mismatch detected', {
      questId: currentQuestId,
      workshopStep,
      benchmark,
      validationPoint,
      expected,
      actual,
      mismatches,
    });
  }

  return {
    isValid: !hasMismatches,
    expected,
    actual,
    mismatches,
  };
};
